import React, { useState } from "react";
import { PageHeader } from "@finitless/shared/components";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../ui/UIElements/Icon";
import { useGlobalUtils } from "../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import {
  STATUS_LABELS,
  STATUS_BADGE_COLORS,
  getStatusGroup,
} from "@finitless/shared/orderConstants";
import { useOrderDetail } from "../../../hooks/useOrderDetail";
import { useOrderSocketUpdates } from "../../../hooks/useOrderSocketUpdates";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import ConversationAiModal from "../../ui/Modals/ConversationAiModal";
import { Button } from "../../ui/UIElements/Button";
import "./orders-pages.css";

const OrderDetailPage = ({ initialOrder = null }) => {
  const { t } = useTranslation("orders");
  const { id } = useParams();
  const orderId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [showConversationModal, setShowConversationModal] = useState(false);
  const { parseDate, parsePrice, calculateEstimatedDelivery } =
    useGlobalUtils();

  useOrderSocketUpdates();

  // Get order data from navigation state or props
  const orderFromState = location.state?.order;
  const orderData = initialOrder || orderFromState;

  // Fetch order data using custom hook
  const {
    data: order,
    isLoading,
    error,
    isError,
  } = useOrderDetail(orderId, orderData);

  // Helper functions for included taxes
  const getIncludedTaxes = (isDeliveryFee = false) => {
    if (!order) return 0;
    if (order?.taxes?.length === 0) {
      return order?.tax_type === 1 ? (order?.summary?.tax ?? 0) : 0;
    } else {
      return order?.taxes?.reduce((taxIncluded, tax) => {
        return (
          taxIncluded +
          ((!isDeliveryFee && tax.type === 1 && tax.target === "product") ||
          (isDeliveryFee && tax.type === 1 && tax.target === "delivery_fee")
            ? tax.summary?.tax
            : 0)
        );
      }, 0);
    }
  };

  const getIncludedTaxesDiscounts = () => {
    if (!order) return 0;
    return (
      order?.taxes
        ?.filter((tax) => tax?.type === 1 && tax?.target === "product")
        ?.reduce(
          (carry, tax) =>
            carry + (tax?.summary?.tax_after_discount ?? tax?.summary?.tax),
          0,
        ) ?? 0
    );
  };

  const handleBackToOrders = () => {
    navigate("/orders");
  };

  const handleViewConversation = () => {
    setShowConversationModal(true);
  };

  const handleCloseConversationModal = () => {
    setShowConversationModal(false);
  };

  if (isLoading) {
    return (
      <div
        className="ordering-order-detail flex min-h-96 items-center justify-center"
        data-ordering-surface="order-detail-loading"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 motion-reduce:animate-none"></div>
          <p className="text-muted-foreground">{t("detail.states.loading")}</p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !order)) {
    return (
      <div
        className="ordering-order-detail border-border bg-card rounded-lg border px-4 py-12 text-center shadow-[var(--od-shadow-1)]"
        data-ordering-surface="order-detail-error"
        role="alert"
      >
        <Icon
          name="ShoppingBag"
          className="text-muted-foreground mx-auto mb-4 h-12 w-12"
        />
        <h3 className="text-foreground mt-2 text-sm font-medium">
          {t("detail.states.notFoundTitle")}
        </h3>
        <p className="text-muted-foreground mt-1 text-sm">
          {error?.message || t("detail.states.notFoundDescription")}
        </p>
        <Button
          type="button"
          onClick={handleBackToOrders}
          emphasis="solid"
          intent="brand"
          size="touch"
          className="mt-4"
        >
          {t("detail.actions.backToOrders")}
        </Button>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusGroup = getStatusGroup(status);
    const statusLabel = t(`statusLabels.${status}`, {
      defaultValue: STATUS_LABELS[status] || status,
    });

    return (
      <span
        data-order-status={statusGroup}
        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
          STATUS_BADGE_COLORS[statusGroup] || STATUS_BADGE_COLORS.unknown
        }`}
      >
        {statusLabel}
      </span>
    );
  };

  const breadcrumbItems = [
    { label: t("header.title"), path: "/orders", icon: "ShoppingBag" },
    {
      label: order
        ? t("card.orderNumber", { id: order.id })
        : t("detail.states.loadingShort"),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div
      className="ordering-order-detail space-y-4 sm:space-y-6"
      data-ordering-surface="order-detail"
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header with Actions */}
      <div className="flex items-center justify-end">
        <div></div>
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={handleViewConversation}
            emphasis="solid"
            intent="brand"
            size="touch"
          >
            <Icon name="MessageCircle" className="h-4 w-4" />
            <span>{t("detail.actions.viewConversation")}</span>
          </Button>
        </div>
      </div>

      {/* Order Header */}
      <section className="border-border bg-card rounded-lg border p-4 shadow-[var(--od-shadow-1)] sm:p-6">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PageHeader
            title={t("card.orderNumber", { id: order?.id })}
            subtitle={t("detail.header.orderDetails")}
          />
          <div className="text-start sm:text-end">
            <div className="text-primary font-mono text-3xl font-bold tabular-nums">
              {parsePrice(order.summary?.total) || "0.00"}
            </div>
            <p className="text-muted-foreground text-sm">
              {t("detail.labels.totalAmount")}
            </p>
          </div>
        </div>

        {/* Order Status */}
        <div
          className="flex items-center justify-between"
          data-ordering-detail-row=""
        >
          <span className="text-muted-foreground">
            {t("detail.labels.status")}:
          </span>
          {getStatusBadge(order.status)}
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Customer Information */}
        <section className="border-border bg-card rounded-lg border p-4 shadow-[var(--od-shadow-1)] sm:p-6">
          <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
            <Icon name="User" className="h-5 w-5" />
            {t("detail.sections.customerInformation")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.name")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {order.customer?.name} {order.customer?.lastname}
              </span>
            </div>
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.phone")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {order.customer?.country_phone_code
                  ? `+${order.customer.country_phone_code}`
                  : ""}{" "}
                {order.customer?.cellphone}
              </span>
            </div>
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.email")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {order.customer?.email || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </section>

        {/* Delivery Information */}
        <section className="border-border bg-card rounded-lg border p-4 shadow-[var(--od-shadow-1)] sm:p-6">
          <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
            <Icon name="Truck" className="h-5 w-5" />
            {t("detail.sections.deliveryInformation")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.address")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {order?.customer?.address || t("common.notAvailable")}
              </span>
            </div>
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.orderTime")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {parseDate(order?.created_at, { useUTC: true })}
              </span>
            </div>
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.estimatedDelivery")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {parseDate(calculateEstimatedDelivery(order))}
              </span>
            </div>
            <div className="flex justify-between" data-ordering-detail-row="">
              <span className="text-muted-foreground">
                {t("detail.labels.paymentMethod")}:
              </span>
              <span className="text-foreground" data-ordering-detail-value="">
                {order.paymethod?.name || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </section>
      </div>

      {/* Order Items */}
      <div className="border-border bg-background rounded-lg border p-6">
        <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
          <Icon name="ShoppingBag" className="mr-2 h-5 w-5" />
          {t("detail.sections.orderItems")}
        </h3>
        <div className="space-y-3">
          {(order.products || []).map((product, index) => (
            <div key={product.id || index} className="bg-card rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-foreground font-medium">
                    {t("detail.labels.productWithQuantity", {
                      quantity: product.quantity,
                      name: product.name,
                    })}
                  </h4>
                  {product.comment && (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {t("detail.labels.note")}: {product.comment}
                    </p>
                  )}
                  {/* Show options if available */}
                  {product.options && product.options.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {product.options.map((option, optIndex) => (
                        <div key={optIndex} className="text-foreground text-sm">
                          <span className="text-muted-foreground">
                            {option.name}:
                          </span>
                          {option.suboptions?.map((suboption, subIndex) => (
                            <span key={subIndex} className="ml-1">
                              {suboption.name}
                              {suboption.price > 0 &&
                                ` (+${parsePrice(suboption.price)})`}
                              {subIndex < option.suboptions.length - 1 && ", "}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="ml-4 text-right">
                  <div className="text-foreground font-medium">
                    {parsePrice(product.total || product.summary?.total || 0)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="border-border mt-6 space-y-2 border-t pt-4">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("detail.summary.subtotal")}:
            </span>
            <span className="text-foreground">
              {parsePrice(
                (order?.summary?.subtotal ?? order?.subtotal ?? 0) +
                  getIncludedTaxes(),
              )}
            </span>
          </div>
          {(order?.summary?.discount > 0 || order?.discount > 0) &&
            !order?.offers?.length && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {order?.offer_type === 1 && order?.offer_rate ? (
                      <>
                        {t("detail.summary.discount")} ({order.offer_rate}%)
                      </>
                    ) : (
                      <>{t("detail.summary.discount")}</>
                    )}
                    :
                  </span>
                  <span className="text-success">
                    -
                    {parsePrice(
                      order?.summary?.discount ?? order?.discount ?? 0,
                    )}
                  </span>
                </div>
                {order?.summary?.subtotal_with_discount > 0 &&
                  order?.summary?.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t("detail.summary.subtotalAfterDiscount")}:
                      </span>
                      <span className="text-foreground">
                        $
                        {parsePrice(
                          order?.tax_type === 1
                            ? order.summary.subtotal_with_discount +
                                getIncludedTaxesDiscounts()
                            : order.summary.subtotal_with_discount,
                        )}
                      </span>
                    </div>
                  )}
              </>
            )}
          {order?.offers?.length > 0 &&
            order?.offers
              ?.filter((offer) => offer?.target === 1)
              ?.map((offer) => (
                <React.Fragment key={offer.id}>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {offer.name}
                      {offer.rate_type === 1 && offer?.rate && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({offer.rate}%)
                        </span>
                      )}
                      :
                    </span>
                    <span className="text-success">
                      -{parsePrice(offer?.summary?.discount ?? 0)}
                    </span>
                  </div>
                </React.Fragment>
              ))}
          {order?.taxes?.length === 0 &&
            order?.tax_type === 2 &&
            order?.summary?.tax > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("detail.summary.tax")}
                  {order?.tax && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({order.tax}%)
                    </span>
                  )}
                  :
                </span>
                <span className="text-foreground">
                  {parsePrice(order.summary.tax)}
                </span>
              </div>
            )}
          {order?.fees?.length === 0 && order?.summary?.service_fee > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.summary.serviceFee")}
                {order?.service_fee && (
                  <span className="text-muted-foreground">
                    {" "}
                    ({order.service_fee}%)
                  </span>
                )}
                :
              </span>
              <span className="text-foreground">
                {parsePrice(order.summary.service_fee)}
              </span>
            </div>
          )}
          {order?.taxes?.length > 0 &&
            order?.taxes
              ?.filter(
                (tax) =>
                  tax?.type === 2 &&
                  tax?.rate !== 0 &&
                  tax?.target === "product",
              )
              .map((tax) => (
                <div key={tax?.id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {tax?.name || t("detail.summary.tax")}
                    {tax?.rate && (
                      <span className="text-muted-foreground">
                        {" "}
                        ({tax.rate}%)
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-foreground">
                    {parsePrice(
                      tax?.summary?.tax_after_discount ??
                        tax?.summary?.tax ??
                        0,
                    )}
                  </span>
                </div>
              ))}
          {order?.fees?.length > 0 &&
            order?.fees
              ?.filter((fee) => !(fee?.fixed === 0 && fee?.percentage === 0))
              ?.map((fee) => (
                <div key={fee.id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {fee?.name || t("detail.summary.fee")}
                    {(fee?.fixed > 0 || fee?.percentage > 0) && (
                      <span className="text-muted-foreground">
                        {" "}
                        (
                        {fee?.fixed > 0 &&
                          `$${fee.fixed.toFixed(2)}${fee.percentage > 0 ? " + " : ""}`}
                        {fee.percentage > 0 && `${fee.percentage}%`})
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-foreground">
                    {parsePrice(
                      (fee?.summary?.fixed ?? 0) +
                        (fee?.summary?.percentage_after_discount ??
                          fee?.summary?.percentage ??
                          0),
                    )}
                  </span>
                </div>
              ))}
          {order?.offers?.length > 0 &&
            order?.offers
              ?.filter((offer) => offer?.target === 3)
              ?.map((offer) => (
                <div key={offer.id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {offer.name}
                    {offer.rate_type === 1 && offer?.rate && (
                      <span className="text-muted-foreground">
                        {" "}
                        ({offer.rate}%)
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-success">
                    -{parsePrice(offer?.summary?.discount ?? 0)}
                  </span>
                </div>
              ))}
          {order?.summary?.delivery_price !== undefined &&
            order?.summary?.delivery_price !== null &&
            order?.delivery_type !== 2 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("detail.summary.deliveryFee")}:
                </span>
                <span className="text-foreground">
                  {parsePrice(
                    order.summary.delivery_price + getIncludedTaxes(true),
                  )}
                </span>
              </div>
            )}
          {order?.extra_value_checkprice &&
            order?.extra_value_checkprice > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("detail.summary.extraValueCheckprice")}:
                </span>
                <span className="text-foreground">
                  {parsePrice(order.extra_value_checkprice)}
                </span>
              </div>
            )}
          {order?.taxes?.length > 0 &&
            order?.taxes
              ?.filter(
                (tax) =>
                  tax?.type === 2 &&
                  tax?.rate !== 0 &&
                  tax?.target === "delivery_fee",
              )
              .map((tax) => (
                <div key={tax?.id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {tax?.name || t("detail.summary.tax")}
                    {tax?.rate && (
                      <span className="text-muted-foreground">
                        {" "}
                        ({tax.rate}%)
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-foreground">
                    {parsePrice(
                      tax?.summary?.tax_after_discount ??
                        tax?.summary?.tax ??
                        0,
                    )}
                  </span>
                </div>
              ))}
          {order?.offers?.length > 0 &&
            order?.offers
              ?.filter((offer) => offer?.target === 2)
              ?.map((offer) => (
                <div key={offer.id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {offer.name}
                    {offer.rate_type === 1 && offer?.rate && (
                      <span className="text-muted-foreground">
                        {" "}
                        ({offer.rate}%)
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-success">
                    -{parsePrice(offer?.summary?.discount ?? 0)}
                  </span>
                </div>
              ))}
          {(order?.summary?.driver_tip > 0 || order?.driver_tip > 0) && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.summary.driverTip")}
                {order?.driver_tip_rate && (
                  <span className="text-muted-foreground">
                    {" "}
                    ({order.driver_tip_rate}%)
                  </span>
                )}
                :
              </span>
              <span className="text-foreground">
                {parsePrice(
                  order?.summary?.driver_tip ?? order?.totalDriverTip ?? 0,
                )}
              </span>
            </div>
          )}
          {order?.metafields
            ?.filter(
              (meta) =>
                meta?.key?.includes("extra_value") &&
                ![0, -0].includes(parseFloat(meta?.value)),
            )
            ?.map((extra, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-muted-foreground">{extra?.key}:</span>
                <span className="text-foreground">
                  {parseFloat(extra?.value) > 0
                    ? parsePrice(parseFloat(extra.value))
                    : `-${parsePrice(parseFloat(extra.value) * -1)}`}
                </span>
              </div>
            ))}
          <div className="border-border flex justify-between border-t pt-2 text-xl font-bold">
            <span className="text-foreground">
              {t("detail.summary.total")}:
            </span>
            <span className="text-primary font-mono tabular-nums">
              {parsePrice(order?.summary?.total ?? 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Special Instructions */}
      {order.comment && (
        <div className="border-border bg-background rounded-lg border p-6">
          <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
            <Icon name="FileText" className="mr-2 h-5 w-5" />
            {t("detail.sections.specialInstructions")}
          </h3>
          <div className="bg-card rounded-lg p-4">
            <p className="text-foreground">{order.comment}</p>
          </div>
        </div>
      )}

      {/* Business Information */}
      {order.business && (
        <div className="border-border bg-background rounded-lg border p-6">
          <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
            <Icon name="Building" className="mr-2 h-5 w-5" />
            {t("detail.sections.businessInformation")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.businessName")}:
              </span>
              <span className="text-foreground">{order.business.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.address")}:
              </span>
              <span className="text-foreground text-right">
                {order.business.address}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.phone")}:
              </span>
              <span className="text-foreground">
                {order.business.cellphone || order.business.phone}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Conversation Modal */}
      <ConversationAiModal
        order={order}
        isOpen={showConversationModal}
        onClose={handleCloseConversationModal}
      />
    </div>
  );
};

export default OrderDetailPage;
